import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import {
  HttpService,
  NotificationService,
  SalesInvoiceEmailContacts,
  SalesInvoiceItems,
  SalesInvoiceReport,
  TmsWebBaseEditCreateService,
} from 'tms-lib';
import {
  SalesInvoiceForCreditNote,
  SalesInvoiceHeaderDetails,
  SalesInvoiceHeader,
  SalesInvoicePaymentDueDate,
  SalesInvoicesCreated,
  SalesInvoiceCreditNotes,
  SagePosting,
  SalesCommissionInvoicesCreated,
  SalesInvoiceItemAddRemove,
} from '../../../models';

@Injectable({
  providedIn: 'root',
})
export class SalesInvoiceHeaderService extends TmsWebBaseEditCreateService<
  SalesInvoiceHeader,
  SalesInvoiceHeaderDetails
> {
  public refreshDetails = new Subject<void>();

  constructor(
    protected httpService: HttpService,
    protected notificationService: NotificationService,
  ) {
    super('SalesInvoiceHeader', $localize`Sales Invoice`, httpService, notificationService);
  }

  createSalesInvoice(ids: string[]): Observable<string[]> {
    return this.httpService.post<string[]>(`${this.apiUrl}/CreateSalesInvoice`, { ids });
  }

  createCommissionSalesInvoice(ids: string[]): Observable<string[]> {
    return this.httpService.post<string[]>(`${this.apiUrl}/CreateCommissionSalesInvoice`, { ids });
  }

  getCreatedSalesInvoices(ids: string[]): Observable<SalesInvoicesCreated[]> {
    return this.httpService.post<SalesInvoicesCreated[]>(`${this.apiUrl}/CreatedSalesInvoiceHeaders`, { ids });
  }

  getCreatedSalesCommissionInvoices(ids: string[]): Observable<SalesCommissionInvoicesCreated[]> {
    return this.httpService.post<SalesCommissionInvoicesCreated[]>(
      `${this.apiUrl}/CreatedSalesCommissionInvoiceHeaders`,
      { ids },
    );
  }

  getSalesInvoiceReport(id: string): Observable<SalesInvoiceReport> {
    return this.httpService.get<SalesInvoiceReport>(`${this.apiUrl}/SalesInvoiceReport/${id}`);
  }

  getSalesInvoiceItemsForInvoice(id: string): Observable<SalesInvoiceItems> {
    return this.httpService.get<SalesInvoiceItems>(`${this.apiUrl}/SalesInvoiceItemsForInvoice?id=${id}`);
  }

  getSalesInvoiceEmailContacts(id: string): Observable<SalesInvoiceEmailContacts> {
    return this.httpService.get<SalesInvoiceEmailContacts>(`${this.apiUrl}/SalesInvoiceEmailContacts?id=${id}`);
  }

  getPaymentDueDate(model: SalesInvoicePaymentDueDate): Observable<SalesInvoicePaymentDueDate> {
    return this.httpService.post<SalesInvoicePaymentDueDate>(`${this.apiUrl}/GetPaymentDueDate`, model);
  }

  getSalesInvoicesForCreditNote(search: string): Observable<SalesInvoiceForCreditNote[]> {
    search = search?.trim();

    return this.httpService.get<SalesInvoiceForCreditNote[]>(
      `${this.apiUrl}/SalesInvoicesForCreditNote?search=${search}`,
    );
  }

  getSalesInvoicesForPosting(): Observable<SagePosting[]> {
    return this.httpService.get<SagePosting[]>(`${this.apiUrl}/SalesInvoicesForPosting`);
  }

  postSalesInvoices(ids: string[]): Observable<SagePosting[]> {
    return this.httpService.post<SagePosting[]>(`${this.apiUrl}/PostSalesInvoices`, { ids });
  }

  emailCSVFile(): Observable<any> {
    return this.httpService.get(`${this.apiUrl}/EmailCSVFile`);
  }

  emailSalesAnalysisCSVFile(fromDate: Date, toDate: Date): Observable<any> {
    return this.httpService.get(
      `${this.apiUrl}/EmailSalesAnalysisCSVFile?fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}`,
    );
  }

  getSalesInvoiceCreditNotes(fromDate: Date, toDate: Date): Observable<SalesInvoiceCreditNotes[]> {
    return this.httpService.get<SalesInvoiceCreditNotes[]>(
      `${this.apiUrl}/SalesInvoiceCreditNotes?fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}`,
    );
  }

  updateSalesInvoicePrice(id: string): Observable<string> {
    return this.httpService.post<string>(`${this.apiUrl}/UpdateSalesInvoicePrice`, { id });
  }

  removeFromSalesInvoice(salesInvoiceItemAddRemove: SalesInvoiceItemAddRemove): Observable<string> {
    return this.httpService.post<string>(`${this.apiUrl}/RemoveFromSalesInvoice`, salesInvoiceItemAddRemove);
  }

  addToSalesInvoice(salesInvoiceItemAddRemove: SalesInvoiceItemAddRemove): Observable<string> {
    return this.httpService.post<string>(`${this.apiUrl}/AddToSalesInvoice`, salesInvoiceItemAddRemove);
  }
}
